/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { LayoutGrid, Rows3 } from "lucide-react";
import { useStoredChoice } from "../lib/useStoredChoice";
import { cn } from "../lib/utils";
import { Tooltip } from "./ui/Tooltip";

export type ListView = "table" | "cards";

const STORAGE_PREFIX = "view:";

/** The list's view, remembered per page in this browser; the page's own default until chosen. */
export function useListView(key: string, fallback: ListView): [ListView, (next: ListView) => void] {
  return useStoredChoice<ListView>(STORAGE_PREFIX + key, fallback, ["table", "cards"]);
}

const VIEWS: { value: ListView; label: string; hint: string; icon: typeof Rows3 }[] = [
  { value: "table", label: "Table", hint: "Show as a table", icon: Rows3 },
  { value: "cards", label: "Cards", hint: "Show as cards", icon: LayoutGrid },
];

export function ViewToggle({
  value,
  onChange,
}: {
  value: ListView;
  onChange: (next: ListView) => void;
}) {
  return (
    <div
      role="group"
      aria-label="View"
      className="flex h-11 items-center gap-0.5 rounded-2xl border border-border bg-bg-base p-0.5"
    >
      {VIEWS.map(({ value: view, label, hint, icon: Icon }) => (
        <Tooltip key={view} content={hint}>
          <button
            type="button"
            aria-label={label}
            aria-pressed={value === view}
            onClick={() => onChange(view)}
            className={cn(
              "grid size-9 cursor-pointer place-items-center rounded-xl transition-colors",
              value === view
                ? "border border-border-hover bg-bg-elevated text-text-primary shadow-light"
                : "text-text-tertiary hover:text-text-primary",
            )}
          >
            <Icon size={16} />
          </button>
        </Tooltip>
      ))}
    </div>
  );
}
